import { Injectable } from '@angular/core';
import { environment } from '../environments/environment';

/**
 * Resolves whether the app is being served on the public apex (no subdomain)
 * or on a specific tenant's subdomain, and builds tenant-scoped URLs.
 *
 * Apex examples (=> public portal): menassaty.com, www.menassaty.com, localhost,
 * 127.0.0.1. Tenant examples: acme.menassaty.com, acme.localhost.
 */
@Injectable({ providedIn: 'root' })
export class TenantHostService {
  private readonly baseDomain = (environment.baseDomain || 'localhost').toLowerCase();

  /** Current hostname without the port, lowercased. */
  private get host(): string {
    return window.location.hostname.toLowerCase();
  }

  /**
   * Returns the tenant subdomain label, or null when on the apex/public host.
   * `www` is treated as the apex.
   */
  getSubdomain(): string | null {
    const host = this.host;

    // Bare base domain or plain localhost => apex.
    if (host === this.baseDomain || host === 'localhost') return null;

    // IP addresses have no subdomain concept => treat as apex.
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(host)) return null;

    // Must be a child of the configured base domain to carry a subdomain.
    const suffix = '.' + this.baseDomain;
    if (!host.endsWith(suffix)) return null;

    const label = host.slice(0, -suffix.length);
    if (!label || label === 'www') return null;

    // Only the left-most label is the tenant (ignore deeper nesting).
    return label.split('.')[0];
  }

  /** True when serving the public portal (no tenant subdomain). */
  isApex(): boolean {
    return this.getSubdomain() === null;
  }

  /**
   * Builds the absolute login URL for a given tenant subdomain, preserving the
   * current protocol and port. e.g. ('acme') => https://acme.menassaty.com/account/login
   * (or http://acme.localhost:4200/account/login in dev).
   */
  buildTenantLoginUrl(subdomain: string): string {
    const sub = subdomain.trim().toLowerCase();
    const { protocol, port } = window.location;
    const portPart = port ? `:${port}` : '';
    return `${protocol}//${sub}.${this.baseDomain}${portPart}/account/login`;
  }

  /** Builds the absolute origin for a tenant subdomain (used for tenant lookup). */
  buildTenantOrigin(subdomain: string): string {
    const sub = subdomain.trim().toLowerCase();
    const { protocol, port } = window.location;
    const portPart = port ? `:${port}` : '';
    return `${protocol}//${sub}.${this.baseDomain}${portPart}`;
  }
}
